import {useCallback, useEffect, useRef, useState} from 'react';
import {customAlert} from '@/components/atoms/CustomAlert';

type UsePreventNavigationProps = {
  isLocked: boolean;
  isSilent?: boolean;
  captureBackButtonCallback?: () => void;
};

/**
 * usePreventNavigation is a React hook that helps to restrict or block navigation
 * actions such as browser back/forward navigation or page refresh when a specific
 * condition is met. It works by utilizing the browser's `history` and `popstate` APIs,
 * manipulating the navigation stack to "lock" users on the current page until they are
 * allowed to navigate or confirm their intention.
 *
 * @param props Configuration options for the hook.
 * @param props.isLocked Determines whether navigation prevention is enabled.
 *                                  If true, users are blocked from navigating away.
 * @param props.isSilent If true, navigation restrictions occur silently
 *                                         without showing a warning dialog to users.
 *                                         Default: `false`.
 * @param props.captureBackButtonCallback Optional callback function invoked
 *                                                     when the browser back button is pressed.
 */
export const usePreventNavigation = ({
  isLocked,
  isSilent = false,
  captureBackButtonCallback = () => undefined,
}: UsePreventNavigationProps) => {
  const [isLockedReady, setIsLockedReady] = useState(isLocked);
  const pathnames = useRef<string[]>([]);

  // This function adds a new history entry using `window.history.pushState`
  // without changing the current URL. This effectively "locks" the user to
  // the current page because the browser cannot navigate away without
  // triggering the `popstate` event.
  const lockNavigation = useCallback(() => {
    const currentHref = window.location.href;
    if (pathnames.current.at(-1) !== currentHref) {
      pathnames.current.push(currentHref);
    }
    window.history.pushState(null, document.title, currentHref);
  }, []);

  // Reset state when allowed to navigate away
  const restoreLastNavigation = useCallback(() => {
    // Remove the last pathname from the stack
    pathnames.current.pop();

    if (pathnames.current.length > 0) {
      const previousHref = pathnames.current.at(-1);
      // TODO: consider using window.history.back() when prevHref is same as current
      window.history.replaceState(null, document.title, previousHref);
    } else {
      if (isLocked) {
        window.history.go(-2); // unlock
      } else {
        window.history.back();
      }
    }
  }, [isLocked]);

  // Allow leaving the page
  const leavePage = useCallback(() => {
    restoreLastNavigation();
  }, [restoreLastNavigation]);

  // Handle back/forward navigation (popstate events)
  const handlePopState = useCallback(async () => {
    if (pathnames.current.length === 0) return; // No history to process

    // Prevent navigating back when user press back button.
    lockNavigation();

    if (isSilent) {
      captureBackButtonCallback?.();
      return;
    }

    await customAlert(
      'Are you sure you want to leave?',
      'Your progress will be lost if you navigate away from this page. Do you want to continue?',
      [
        {text: 'No', onPress: () => {}},
        {
          text: 'Yes',
          onPress: () => {
            captureBackButtonCallback?.();
            leavePage();
          },
        },
      ]
    );
  }, [captureBackButtonCallback, lockNavigation, isSilent, leavePage]);

  // Capture refresh or tab closing
  const handleBeforeUnload = useCallback((e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = true;
  }, []);

  // STEP 1: Set back button pathnames.
  // Update the `pathnames` array when locked
  useEffect(() => {
    if (isLocked) {
      if (pathnames.current.length === 0 || pathnames.current.at(-1) !== window.location.pathname) {
        pathnames.current.push(window.location.href);
      }
    } else {
      pathnames.current = [];
    }

    // Wait before marking as ready to avoid potential runtime issues.
    const timeout = setTimeout(() => setIsLockedReady(isLocked), 2000);

    return () => clearTimeout(timeout);
  }, [isLocked]);

  // Enforce navigation restrictions if "isLocked" is enabled
  useEffect(() => {
    if (!isLockedReady) {
      return;
    }
    lockNavigation();
  }, [isLockedReady, lockNavigation]);

  // Add/remove event listeners for navigation restrictions
  useEffect(() => {
    if (!isLockedReady) {
      return;
    }

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handlePopState, handleBeforeUnload, isLockedReady]);
};
